import { useState, useMemo } from "react";
import { Trash01, Edit01 } from "@untitledui/icons";
import { useChatAI, type ChatHistory } from "./chat-ai-context";
import { Button } from "@/components/base/buttons/button";
import { Input } from "@/components/base/input/input";
import { cx } from "@/utils/cx";
import { useTranslation } from "react-i18next";

const formatTimestamp = (timestamp: number): string => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) {
        return "Adesso";
    } else if (diffMins < 60) {
        return `${diffMins}m fa`;
    } else if (diffHours < 24) {
        return `${diffHours}h fa`;
    } else if (diffDays < 7) {
        return `${diffDays}g fa`;
    } else {
        return date.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
        });
    }
};

const getChatPreview = (chat: ChatHistory, t: (key: string) => string): string => {
    if (chat.messages.length === 0) {
        return t('aiChat.newChat');
    }
    // Get first user message or assistant message
    const firstMessage = chat.messages[0];
    if (firstMessage) {
        const preview = firstMessage.content.slice(0, 60);
        return preview.length < firstMessage.content.length ? `${preview}...` : preview;
    }
    return t('aiChat.newChat');
};

const getChatTitle = (chat: ChatHistory, t: (key: string) => string): string => {
    if (chat.title) {
        return chat.title;
    }
    return getChatPreview(chat, t);
};

interface ChatHistoryItemProps {
    chat: ChatHistory;
    isActive: boolean;
    onSelect: (chatId: string) => void;
    onDelete: (chatId: string) => void;
    onUpdateTitle: (chatId: string, title: string) => void;
}

const ChatHistoryItem = ({ chat, isActive, onSelect, onDelete, onUpdateTitle }: ChatHistoryItemProps) => {
    const { t } = useTranslation(['common']);
    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState(chat.title || getChatPreview(chat, t));
    const preview = getChatPreview(chat, t);
    const timestamp = formatTimestamp(chat.timestamp);
    const displayTitle = getChatTitle(chat, t);

    const handleClick = (e: React.MouseEvent) => {
        if (isEditing) {
            e.stopPropagation();
            return;
        }
        e.preventDefault();
        onSelect(chat.id);
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (window.confirm(t('aiChat.confirmDelete'))) {
            onDelete(chat.id);
        }
    };

    const handleEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setIsEditing(true);
        setEditTitle(displayTitle);
    };

    const handleSaveTitle = (e: React.MouseEvent) => {
        e.stopPropagation();
        onUpdateTitle(chat.id, editTitle);
        setIsEditing(false);
    };

    const handleCancelEdit = (e: React.MouseEvent) => {
        e.stopPropagation();
        setEditTitle(displayTitle);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            e.preventDefault();
            handleSaveTitle(e as any);
        } else if (e.key === "Escape") {
            e.preventDefault();
            handleCancelEdit(e as any);
        }
    };

    return (
        <div
            className={cx(
                "group relative flex w-full cursor-pointer items-start rounded-md px-4 py-3 outline-focus-ring transition duration-100 ease-linear select-none",
                "hover:bg-primary_hover focus-visible:z-10 focus-visible:outline-2 focus-visible:outline-offset-2 border border-secondary",
                isActive && "bg-active hover:bg-secondary_hover border-brand"
            )}
            onClick={handleClick}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (!isEditing) {
                        onSelect(chat.id);
                    }
                }
            }}
        >
            <div className="flex-1 min-w-0">
                {isEditing ? (
                    <div className="flex items-center gap-2 mb-2">
                        <Input
                            value={editTitle}
                            onChange={(val) => setEditTitle(val as string)}
                            onKeyDown={handleKeyDown}
                            className="flex-1"
                            autoFocus
                        />
                        <Button
                            onClick={handleSaveTitle}
                            size="sm"
                            color="primary"
                        >
                            {t('aiChat.saveTitle')}
                        </Button>
                        <Button
                            onClick={handleCancelEdit}
                            size="sm"
                            color="link-color"
                        >
                            {t('buttons.cancel')}
                        </Button>
                    </div>
                ) : (
                    <>
                        <div className="flex items-baseline justify-between gap-2 mb-1">
                            <h4
                                className={cx(
                                    "text-base font-semibold truncate",
                                    isActive ? "text-secondary_hover" : "text-secondary group-hover:text-secondary_hover"
                                )}
                            >
                                {displayTitle}
                            </h4>
                            <div className="flex items-center gap-1 shrink-0">
                                <button
                                    type="button"
                                    onClick={handleEdit}
                                    className={cx(
                                        "opacity-0 group-hover:opacity-100 shrink-0 p-1.5 rounded-md transition-opacity",
                                        "hover:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
                                        "text-tertiary hover:text-secondary"
                                    )}
                                    aria-label={t('aiChat.editTitle')}
                                >
                                    <Edit01 className="size-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDelete}
                                    className={cx(
                                        "opacity-0 group-hover:opacity-100 shrink-0 p-1.5 rounded-md transition-opacity",
                                        "hover:bg-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring",
                                        "text-tertiary hover:text-secondary"
                                    )}
                                    aria-label={t('aiChat.deleteChat')}
                                >
                                    <Trash01 className="size-4" />
                                </button>
                            </div>
                        </div>
                        {preview !== displayTitle && (
                            <p className="text-sm text-tertiary truncate mb-1">{preview}</p>
                        )}
                    </>
                )}
                <p className="text-xs text-tertiary">{timestamp}</p>
            </div>
        </div>
    );
};

export const ChatHistoryView = () => {
    const { t } = useTranslation(['common']);
    const { getAllChats, currentChatId, loadChat, deleteChat, updateChatTitle } = useChatAI();
    
    const chats = useMemo(() => getAllChats(), [getAllChats]);

    return (
        <div className="flex h-full min-h-0 flex-col w-full">
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
                {chats.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center">
                        <p className="text-sm text-tertiary">{t('aiChat.noChats')}</p>
                    </div>
                ) : (
                    <div className="space-y-2 w-full">
                        {chats.map((chat) => (
                            <ChatHistoryItem
                                key={chat.id}
                                chat={chat}
                                isActive={chat.id === currentChatId}
                                onSelect={loadChat}
                                onDelete={deleteChat}
                                onUpdateTitle={updateChatTitle}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

